import { Roles } from "../../../constants/roles";
import { FormBase } from "../../../util/form-base-form";

export class NavbarSettingsForm extends FormBase {
    private readonly root: HTMLElement;

    public constructor(root: HTMLElement) {
        super();
        this.root = root;
    }

    public static getRoots(root: ParentNode): HTMLElement[] {
        return new NavbarSettingsForm(document.body).queryElements(
            root,
            `[data-role="${Roles.NAVBAR_CONTENT}"]`,
            HTMLElement,
        );
    }

    public getRoot(): HTMLElement {
        return this.root;
    }

    /**
     * Every settings-toggle button in this root — normally one, but mobile
     * has a second copy inside the hamburger drawer (the header's own icon
     * is hidden there, moved into the drawer instead — see navbar.blade.php /
     * navbar.scss) alongside the desktop header's copy, both opening the
     * same popover.
     */
    public getToggles(): HTMLButtonElement[] {
        return this.queryElements(this.root, `[data-role="${Roles.NAVBAR_SETTINGS_TOGGLE}"]`, HTMLButtonElement);
    }

    public getPopover(): Element | null {
        return this.queryElement(this.root, `[data-role="${Roles.NAVBAR_SETTINGS_POPOVER}"]`, HTMLElement);
    }

    public getOverlay(): HTMLElement | null {
        return this.queryElement(document, `[data-role="${Roles.NAVBAR_SETTINGS_OVERLAY}"]`, HTMLElement);
    }

    public getNavbarTop(): HTMLElement | null {
        return this.queryElement(this.root, ".navbar-top", HTMLElement);
    }

    public getConnectButton(target: EventTarget | Element | null | undefined): HTMLButtonElement | null {
        return this.closest(target, `[data-role="${Roles.NAVBAR_TIKTOK_CONNECT}"]`, HTMLButtonElement);
    }

    public isInitialized(): boolean {
        return this.root.dataset.navbarSettingsInit === "1";
    }

    public markInitialized(): void {
        this.root.dataset.navbarSettingsInit = "1";
    }

    public isOpen(): boolean {
        const popover = this.getPopover();
        return popover instanceof HTMLElement ? !popover.hidden : false;
    }

    public setOpen(open: boolean): void {
        const popover = this.getPopover();
        if (!(popover instanceof HTMLElement)) return;

        if (open) {
            // Mobile positions this popover with position: fixed, anchored to the
            // viewport (not the settings icon — see navbar.scss) so it can't overflow
            // past the left edge on narrow phones. That needs the header's own
            // height measured fresh each open, same as the hamburger drawer does,
            // since it varies with the app name wrapping to 2 lines.
            const navbarTop = this.getNavbarTop();
            if (navbarTop instanceof HTMLElement) {
                this.root.style.setProperty("--navbar-mobile-header-height", `${navbarTop.offsetHeight}px`);
            }
        }

        popover.hidden = !open;

        this.getToggles().forEach((toggle) => {
            toggle.setAttribute("aria-expanded", open ? "true" : "false");
        });

        const overlay = this.getOverlay();
        if (overlay instanceof HTMLElement) {
            overlay.hidden = !open;
        }
    }
}
